import { useEffect, useState } from 'react';
import { contactsApi, companiesApi, usersApi } from '../api';
import { useAuth } from '../hooks/useAuth';
import SearchableSelect from './SearchableSelect';

interface Props {
  contact?: any;
  onClose: () => void;
  onSave: () => void;
}

export default function ContactModal({ contact, onClose, onSave }: Props) {
  const { user } = useAuth();
  const [companies, setCompanies] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [form, setForm] = useState({
    firstName: contact?.firstName || '',
    lastName: contact?.lastName || '',
    middleName: contact?.middleName || '',
    position: contact?.position || '',
    companyId: contact?.companyId || '',
    phones: contact?.phones?.join(', ') || '',
    emails: contact?.emails?.join(', ') || '',
    messengers: contact?.messengers?.join(', ') || '',
    tags: contact?.tags?.join(', ') || '',
    responsibleId: contact?.responsibleId || '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!contact && user?.id && !form.responsibleId) {
      setForm(prev => ({ ...prev, responsibleId: user.id }));
    }
  }, [user]);

  useEffect(() => {
    const load = async () => {
      try {
        const [compRes, usersRes] = await Promise.all([
          companiesApi.list({ limit: 100 }),
          usersApi.list({ limit: 100 }),
        ]);
        setCompanies(compRes.data.data || []);
        setUsers(usersRes.data.data || []);
      } catch {}
    };
    load();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...form,
        phones: form.phones.split(',').map((s: string) => s.trim()).filter(Boolean),
        emails: form.emails.split(',').map((s: string) => s.trim()).filter(Boolean),
        messengers: form.messengers.split(',').map((s: string) => s.trim()).filter(Boolean),
        tags: form.tags.split(',').map((s: string) => s.trim()).filter(Boolean),
        companyId: form.companyId || null,
        responsibleId: form.responsibleId || null,
      };
      if (contact) {
        await contactsApi.update(contact.id, data);
      } else {
        await contactsApi.create(data);
      }
      onSave();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>{contact ? 'Редактировать' : 'Новый контакт'}</h3>
          <button onClick={onClose} style={{ background: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Имя *</label>
                <input className="form-input" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Фамилия *</label>
                <input className="form-input" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Отчество</label>
                <input className="form-input" value={form.middleName} onChange={e => setForm({ ...form, middleName: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Должность</label>
                <input className="form-input" value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} />
              </div>
              <div className="form-group">
                <SearchableSelect
                  label="Компания"
                  value={form.companyId}
                  onChange={val => setForm({ ...form, companyId: val })}
                  options={companies.map((c: any) => ({ value: c.id, label: c.name }))}
                  emptyLabel="— Без компании —"
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Телефоны (через запятую)</label>
                <input className="form-input" value={form.phones} onChange={e => setForm({ ...form, phones: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Email (через запятую)</label>
                <input className="form-input" value={form.emails} onChange={e => setForm({ ...form, emails: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Мессенджеры (через запятую)</label>
              <input className="form-input" value={form.messengers} onChange={e => setForm({ ...form, messengers: e.target.value })} placeholder="telegram: @username" />
            </div>
            <div className="form-group">
              <label className="form-label">Теги (через запятую)</label>
              <input className="form-input" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} />
            </div>
            <div className="form-group">
              <SearchableSelect
                label="Ответственный"
                value={form.responsibleId}
                onChange={val => setForm({ ...form, responsibleId: val })}
                options={users.map((u: any) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }))}
                emptyLabel="— Не назначен —"
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Сохранение...' : 'Сохранить'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
